import React from "react";
import { FiEye, FiTrash2 } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { SkeletonBar, ActionButtonsSkeleton } from "../../../components/skeleton/Skeleton";
import IconButton from "../../../components/button/IconButton";
import { useAuth } from "../../../contexts/authContext";

function getDegree(degrees) {
  if (!degrees) return "";
  if (Array.isArray(degrees)) {
    const first = degrees[0];
    if (!first) return "";
    return typeof first === "string" ? first : first?.name ?? first?.title ?? "";
  }
  return typeof degrees === "string" ? degrees : degrees?.name ?? degrees?.title ?? "";
}

export default function CandidateRow({ candidate, onEdit, onDelete, isLoading = false }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";

  let fullName, position, degree, age, city;

  if (!isLoading && candidate) {
    fullName = candidate.fullName ?? `${candidate.firstName ?? ""} ${candidate.lastName ?? ""}`.trim();
    position = candidate.currentPosition ?? candidate.position ?? "";
    degree = getDegree(candidate.degrees);
    age = candidate.age != null ? String(candidate.age) : "";
    city = candidate.location?.city ?? candidate.city ?? "";
  }

  return (
    <li
      className="
        bg-white rounded-xl border border-gray-200
        px-4 sm:px-6 py-3
        flex items-start justify-between gap-4
        md:grid md:grid-cols-[repeat(5,minmax(0,1fr))_auto]
        md:items-center md:gap-x-10
      "
    >
      <div className="min-w-0 flex-1 md:contents">
        <div className="min-w-0">
          {isLoading ? (
            <div className="h-4 bg-lightGray rounded-lg w-40 animate-pulse" />
          ) : (
            <span className="font-medium text-darkGray text-sm sm:text-base block whitespace-normal break-words line-clamp-2">
              {fullName}
            </span>
          )}
        </div>

        <div className="mt-2 space-y-1 text-sm text-darkGray md:mt-0 md:space-y-0 md:contents">
          {isLoading ? (
            <div className="md:flex md:justify-center">
              <SkeletonBar width="w-28" />
            </div>
          ) : (
            <span className="block md:text-center whitespace-normal break-words line-clamp-2">{position}</span>
          )}

          <div className="md:flex md:justify-center">
            {isLoading ? <SkeletonBar width="w-28" /> : <span className="block md:text-center whitespace-normal break-words line-clamp-2">{degree}</span>}
          </div>

          <span className="truncate block md:hidden">
            {isLoading ? <SkeletonBar width="w-20" /> : [age, city].filter(Boolean).join(" · ")}
          </span>

          {isLoading ? (
            <div className="hidden md:flex md:justify-center">
              <SkeletonBar width="w-24" />
            </div>
          ) : (
            <span className="hidden md:block md:text-center">{age || "-"}</span>
          )}

          {isLoading ? (
            <div className="hidden md:flex md:justify-center">
              <SkeletonBar width="w-24" />
            </div>
          ) : (
            <span className="hidden md:block md:text-center whitespace-normal break-words line-clamp-2">{city}</span>
          )}
        </div>
      </div>

      <div className="flex flex-col items-end gap-2 md:flex-row md:items-center md:justify-self-end md:gap-3">
        {isLoading ? (
          <ActionButtonsSkeleton />
        ) : (
          <>
            <IconButton
              aria-label={t("ACTIONS.VIEW")}
              title={t("ACTIONS.VIEW")}
              onClick={() => onEdit?.(candidate?.id)}
              className="text-gray-700"
            >
              <FiEye className="text-lg" />
            </IconButton>

            {isAdmin && (
              <IconButton
                aria-label={t("ACTIONS.DELETE")}
                title={t("ACTIONS.DELETE")}
                onClick={() => onDelete?.(candidate)}
                className="text-red"
              >
                <FiTrash2 className="text-lg" />
              </IconButton>
            )}
          </>
        )}
      </div>
    </li>
  );
}